import {BookOpen, ArrowRight} from 'lucide-react'
import { lumi } from '../lib/lumi'
import toast from 'react-hot-toast'
import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

// デフォルトのAdmin userID
const DEFAULT_ADMIN_ID = 'SPC00000000000000000000'

export default function LoginPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [isReady, setIsReady] = useState(false)
  const [isLoggingIn, setIsLoggingIn] = useState(false)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    // URLパラメータからmanagerIdを取得し、sessionStorageに保存
    const managerIdParam = searchParams.get('m')
    if (managerIdParam) {
      sessionStorage.setItem('managerId', managerIdParam)
      console.log('📋 マネージャーID保存:', managerIdParam)
    }

    // Lumi認証の初期化を確認
    const checkAuthReady = async () => {
      try {
        // 認証システムの準備完了を待つ
        await new Promise(resolve => setTimeout(resolve, 100))
        setIsReady(true)
      } catch (err) {
        console.error('Auth initialization error:', err)
        setError('認証システムの初期化に失敗しました')
      }
    }
    checkAuthReady()
  }, [])

  const handleLogin = async () => {
    try {
      setError(null)
      setIsLoggingIn(true)
      console.log('Starting login process...')
      const result = await lumi.auth.signIn()
      console.log('Login result:', result)
      
      // ユーザーが新規登録の場合、ウェルカムメールを送信
      if (result?.user) {
        try {
          await lumi.tools.email.send({
            to: result.user.email,
            subject: '英単語学習ソフトへようこそ！',
            fromName: '英単語学習ソフト運営チーム',
            html: `
              <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
                <h2 style="color: #4F46E5;">ようこそ、${result.user.userName}さん！</h2>
                <p>英単語学習ソフトへのご登録ありがとうございます。</p>
                <p>このアプリでは以下の機能をご利用いただけます：</p>
                <ul>
                  <li>中学・高校レベルの英単語学習</li>
                  <li>音声入力練習機能</li>
                  <li>学習進捗の記録と管理</li>
                  <li>ゲーミフィケーション（レベル・バッジ・連続学習日数）</li>
                </ul>
                <p>効果的に英単語を学習して、目標達成を目指しましょう！</p>
                <hr style="border: none; border-top: 1px solid #E5E7EB; margin: 20px 0;">
                <p style="color: #6B7280; font-size: 14px;">
                  ログインID: ${result.user.email}<br>
                  このメールアドレスは大切に保管してください。
                </p>
              </div>
            `,
            text: `ようこそ、${result.user.userName}さん！\n\n英単語学習ソフトへのご登録ありがとうございます。\n\nこのアプリでは以下の機能をご利用いただけます：\n- 中学・高校レベルの英単語学習\n- 音声入力練習機能\n- 学習進捗の記録と管理\n- ゲーミフィケーション（レベル・バッジ・連続学習日数）\n\n効果的に英単語を学習して、目標達成を目指しましょう！\n\nログインID: ${result.user.email}\nこのメールアドレスは大切に保管してください。`
          })
          console.log('Welcome email sent to:', result.user.email)
        } catch (emailError) {
          console.error('Failed to send welcome email:', emailError)
          // メール送信失敗してもログインは成功させる
        }
      }
      
      // Service Worker登録は削除（App.tsxで一元管理）
      
      toast.success('ログインしました')
    } catch (error: any) {
      setIsLoggingIn(false)
      console.error('Login failed:', error)
      console.error('Error details:', {
        name: error?.name,
        code: error?.code,
        message: error?.message,
        stack: error?.stack
      })
      
      // LumiError 10499の詳細を表示
      const errorMsg = error?.code === 10499 
        ? 'ログイン認証エラーが発生しました。ネットワーク接続を確認して、再度お試しください。'
        : 'ログインに失敗しました。ページを再読み込みしてください。'
      
      setError(errorMsg)
      toast.error(errorMsg)
    }
  }

  // エラー表示
  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4 bg-gray-50">
        <div className="max-w-md w-full text-center">
          <div className="mb-8 flex justify-center">
            <div className="w-20 h-20 bg-red-600 rounded-2xl flex items-center justify-center shadow-lg">
              <BookOpen className="w-10 h-10 text-white" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-3">エラー</h1>
          <p className="text-red-600 mb-8">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-4 px-6 rounded-xl transition-colors shadow-lg hover:shadow-xl"
          >
            ページを再読み込み
          </button>
        </div>
      </div>
    )
  }

  // ロード中表示
  if (!isReady) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4 bg-white">
        <div className="max-w-md w-full text-center">
          <div className="mb-8 flex justify-center">
            <div className="w-20 h-20 bg-indigo-600 rounded-2xl flex items-center justify-center shadow-lg animate-pulse">
              <BookOpen className="w-10 h-10 text-white" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-3">読み込み中...</h1>
          <p className="text-gray-600">認証システムを準備しています</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 bg-white">
      <div className="max-w-md w-full text-center">
        <div className="mb-8 flex justify-center">
          <div className="w-20 h-20 bg-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
            <BookOpen className="w-10 h-10 text-white" />
          </div>
        </div>
        
        <h1 className="text-4xl font-bold text-gray-900 mb-3">
          英単語学習ソフト
        </h1>
        <p className="text-gray-600 mb-8">
          効果的に英単語を学習しましょう
        </p>

        <button
          onClick={handleLogin}
          disabled={isLoggingIn}
          className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 disabled:cursor-not-allowed text-white font-semibold py-4 px-6 rounded-xl transition-colors shadow-lg hover:shadow-xl flex items-center justify-center"
        >
          {isLoggingIn ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              認証中...
            </>
          ) : (
            'ログイン / 新規登録'
          )}
        </button>

        <button
          onClick={() => navigate('/landing')}
          className="mt-4 w-full bg-white hover:bg-gray-50 text-indigo-600 font-semibold py-4 px-6 rounded-xl transition-colors shadow-md hover:shadow-lg border-2 border-indigo-200 flex items-center justify-center"
        >
          詳しく知る
          <ArrowRight className="ml-2 w-5 h-5" />
        </button>

        <div className="mt-8 text-sm text-gray-500">
          Lumiプラットフォームの認証システムを使用します
        </div>
      </div>
    </div>
  )
}
